import { useState, useEffect } from "react";
import { useParams, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useI18n } from "@/contexts/I18nContext";
import { Store, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

interface InviteInfo {
  email: string;
  companyName: string;
}

interface Category {
  id: number;
  name: string;
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: "same-origin",
    headers: init?.body ? { "Content-Type": "application/json" } : undefined,
    ...init,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { error?: string };
    throw Object.assign(new Error(body.error ?? "Request failed"), { status: res.status });
  }
  return res.json() as Promise<T>;
}

export default function JoinPage() {
  const params = useParams<{ token: string }>();
  const token = params.token ?? "";
  const [, navigate] = useLocation();
  const { t } = useI18n();
  const { toast } = useToast();

  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [loadingInvite, setLoadingInvite] = useState(true);
  const [categories, setCategories] = useState<Category[]>([]);

  const [companyName, setCompanyName] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [area, setArea] = useState("");
  const [description, setDescription] = useState("");
  const [phone, setPhone] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setInviteError("Invalid invitation link");
      setLoadingInvite(false);
      return;
    }
    Promise.all([
      apiFetch<InviteInfo>(`/api/invitations/${token}`),
      apiFetch<Category[]>("/api/categories"),
    ])
      .then(([info, cats]) => {
        setInviteInfo(info);
        setCompanyName(info.companyName);
        setCategories(cats);
      })
      .catch((err: Error & { status?: number }) => {
        setInviteError(err.message ?? "This invitation is no longer valid");
      })
      .finally(() => setLoadingInvite(false));
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyName.trim() || !categoryId || !area.trim() || !description.trim()) {
      toast({ title: t("Please fill in all required fields"), variant: "destructive" });
      return;
    }
    if (password.length < 8) {
      toast({ title: t("Password must be at least 8 characters"), variant: "destructive" });
      return;
    }
    if (password !== confirmPassword) {
      toast({ title: t("Passwords do not match"), variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await apiFetch(`/api/invitations/${token}/accept`, {
        method: "POST",
        body: JSON.stringify({
          companyName: companyName.trim(),
          categoryId: Number(categoryId),
          area: area.trim(),
          description: description.trim(),
          phone: phone.trim() || null,
          whatsapp: whatsapp.trim() || null,
          password,
        }),
      });
      setDone(true);
      // Short delay so the success state is visible before redirect
      setTimeout(() => { void navigate("/merchant-portal"); }, 2000);
    } catch (err) {
      toast({
        title: t("Could not complete registration"),
        description: err instanceof Error ? err.message : undefined,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (loadingInvite) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // ── Invalid / expired invitation ─────────────────────────────────────────────
  if (inviteError) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <Card className="w-full max-w-sm text-center">
          <CardContent className="pt-8 pb-6 space-y-3">
            <AlertCircle className="w-10 h-10 text-destructive mx-auto" />
            <h2 className="text-lg font-semibold">{t("Invitation not valid")}</h2>
            <p className="text-sm text-muted-foreground">{inviteError}</p>
            <p className="text-sm text-muted-foreground">
              {t("Please ask the Souq Crypto team to send you a new invitation.")}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Success ──────────────────────────────────────────────────────────────────
  if (done) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <Card className="w-full max-w-sm text-center">
          <CardContent className="pt-8 pb-6 space-y-3">
            <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto" />
            <h2 className="text-lg font-semibold">{t("Welcome to Souq Crypto!")}</h2>
            <p className="text-sm text-muted-foreground">
              {t("Your listing has been created. Taking you to your dashboard…")}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Onboarding form ──────────────────────────────────────────────────────────
  return (
    <div className="min-h-[60vh] py-12 px-4 bg-muted/30">
      <div className="max-w-lg mx-auto space-y-6">
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-2">
            <Store className="w-6 h-6 text-primary" />
          </div>
          <h1 className="text-2xl font-bold">{t("Create your listing")}</h1>
          <p className="text-muted-foreground text-sm">
            {t("You're joining Souq Crypto as")} <strong>{inviteInfo?.companyName}</strong>
          </p>
          {inviteInfo?.email && (
            <p className="text-xs text-muted-foreground" dir="ltr">{inviteInfo.email}</p>
          )}
        </div>

        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base">{t("Business details")}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-4">
              {/* Company name */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t("Company name")} *</label>
                <Input
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder={t("Your business name")}
                  required
                  data-testid="input-join-company-name"
                />
              </div>

              {/* Category + Area */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">{t("Category")} *</label>
                  <Select value={categoryId} onValueChange={setCategoryId}>
                    <SelectTrigger data-testid="select-join-category">
                      <SelectValue placeholder={t("Select")} />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={String(cat.id)}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">{t("Area in Dubai")} *</label>
                  <Input
                    value={area}
                    onChange={(e) => setArea(e.target.value)}
                    placeholder="e.g. Deira"
                    required
                    data-testid="input-join-area"
                  />
                </div>
              </div>

              {/* Description */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t("Short description")} *</label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  placeholder={t("Describe your business and what you offer…")}
                  required
                  data-testid="input-join-description"
                />
              </div>

              {/* Contact */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">{t("Phone")}</label>
                  <Input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+971 …"
                    dir="ltr"
                    data-testid="input-join-phone"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">{t("WhatsApp")}</label>
                  <Input
                    value={whatsapp}
                    onChange={(e) => setWhatsapp(e.target.value)}
                    placeholder="+971 …"
                    dir="ltr"
                    data-testid="input-join-whatsapp"
                  />
                </div>
              </div>

              {/* Password section */}
              <div className="border-t pt-4 space-y-3">
                <div>
                  <p className="text-sm font-medium">{t("Set your portal password")}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t("You'll use this to log in and manage your listings at")} <span dir="ltr">/merchant-portal</span>
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("Password")} *</label>
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="new-password"
                      required
                      data-testid="input-join-password"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("Confirm password")} *</label>
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                      required
                      data-testid="input-join-confirm-password"
                    />
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={submitting}
                data-testid="btn-join-submit"
              >
                {submitting ? t("Creating your listing…") : t("Create my listing")}
              </Button>

              <p className="text-xs text-muted-foreground text-center">
                {t("Your listing will be reviewed by the Souq Crypto team before going live.")}
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
